import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Lock, CheckCircle, ChevronRight, Star, Trophy, 
  BookOpen, Clock, Zap, Play, ArrowRight, Award,
  GraduationCap, Languages, Timer
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// A1-A2 Yapılandırılmış Öğrenme Yolu
const LEARNING_PATH = {
  'A1': {
    name: 'A1 - Başlangıç',
    description: 'İngilizce temellerini öğren',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
    units: [
      {
        id: 'a1_unit1',
        name: 'Ünite 1: Günlük Hayat',
        topics: [
          { type: 'vocabulary', id: 'daily_routines', name: 'Günlük Aktiviteler', icon: '🌅' },
          { type: 'vocabulary', id: 'family_friends', name: 'Aile ve Arkadaşlar', icon: '👨‍👩‍👧‍👦' },
          { type: 'grammar', id: 'nouns', name: 'İsimler (Nouns)', icon: '📝' },
          { type: 'grammar', id: 'pronouns', name: 'Zamirler (Pronouns)', icon: '👤' },
          { type: 'tense', id: 'present_simple', name: 'Present Simple', icon: '⏰' },
        ],
        milesReward: 100,
        quiz: { minScore: 70, questions: 10 }
      },
      {
        id: 'a1_unit2',
        name: 'Ünite 2: Çevremiz',
        topics: [
          { type: 'vocabulary', id: 'food_drinks', name: 'Yiyecek ve İçecekler', icon: '🍔' },
          { type: 'vocabulary', id: 'colors_numbers', name: 'Renkler ve Sayılar', icon: '🎨' },
          { type: 'vocabulary', id: 'weather', name: 'Hava Durumu', icon: '☀️' },
          { type: 'grammar', id: 'articles', name: 'A/An/The', icon: '📖' },
          { type: 'grammar', id: 'adjectives', name: 'Sıfatlar (Adjectives)', icon: '✨' },
          { type: 'tense', id: 'present_continuous', name: 'Present Continuous', icon: '🔄' },
        ],
        milesReward: 120,
        quiz: { minScore: 70, questions: 10 }
      }
    ],
    finalExam: { minScore: 70, questions: 20, badge: '🥉 A1 Tamamlandı' }
  },
  'A2': {
    name: 'A2 - Temel',
    description: 'Günlük iletişim becerileri',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    requiresLevel: 'A1',
    units: [
      {
        id: 'a2_unit1',
        name: 'Ünite 1: Sosyal Hayat',
        topics: [
          { type: 'vocabulary', id: 'shopping', name: 'Alışveriş', icon: '🛒' },
          { type: 'vocabulary', id: 'travel_transport', name: 'Seyahat ve Ulaşım', icon: '✈️' },
          { type: 'vocabulary', id: 'jobs_professions', name: 'Meslekler', icon: '👔' },
          { type: 'grammar', id: 'prepositions', name: 'Edatlar (Prepositions)', icon: '📍' },
          { type: 'grammar', id: 'conjunctions', name: 'Bağlaçlar', icon: '🔗' },
          { type: 'tense', id: 'past_simple', name: 'Past Simple', icon: '⏪' },
        ],
        milesReward: 150,
        quiz: { minScore: 70, questions: 10 }
      },
      {
        id: 'a2_unit2',
        name: 'Ünite 2: İletişim',
        topics: [
          { type: 'vocabulary', id: 'technology', name: 'Teknoloji', icon: '💻' },
          { type: 'vocabulary', id: 'emotions_feelings', name: 'Duygular', icon: '😊' },
          { type: 'vocabulary', id: 'health_body', name: 'Sağlık ve Vücut', icon: '🏥' },
          { type: 'grammar', id: 'modal_verbs', name: 'Modal Fiiller', icon: '💪' },
          { type: 'grammar', id: 'comparatives', name: 'Karşılaştırma', icon: '⚖️' },
          { type: 'tense', id: 'future_simple', name: 'Future (will)', icon: '⏩' },
          { type: 'tense', id: 'future_going_to', name: 'Future (going to)', icon: '🎯' },
        ],
        milesReward: 180,
        quiz: { minScore: 70, questions: 10 }
      }
    ],
    finalExam: { minScore: 70, questions: 20, badge: '🥈 A2 Tamamlandı' }
  }
};

// B1-C2 Serbest Erişim Konuları
const FREE_ACCESS_LEVELS = {
  'B1': {
    name: 'B1 - Orta Seviye',
    color: 'from-amber-500 to-orange-600',
    topics: {
      vocabulary: ['environment', 'media_news', 'education', 'relationships', 'culture_traditions', 'money_finance'],
      grammar: ['present_perfect_continuous', 'past_continuous', 'conditionals_first', 'passive_voice_basic', 'relative_clauses', 'reported_speech_basic'],
      tenses: ['present_perfect_continuous', 'past_continuous', 'past_perfect', 'future_continuous']
    }
  },
  'B2': {
    name: 'B2 - Orta-Üst',
    color: 'from-purple-500 to-purple-600',
    topics: {
      vocabulary: ['business_work', 'science_technology', 'politics_society', 'arts_entertainment', 'global_issues', 'psychology'],
      grammar: ['conditionals_second_third', 'passive_voice_advanced', 'modal_perfects', 'causative_verbs', 'mixed_conditionals', 'inversion'],
      tenses: ['all_tenses_review']
    }
  },
  'C1': {
    name: 'C1 - İleri',
    color: 'from-red-500 to-red-600',
    topics: {
      vocabulary: ['philosophy', 'law_justice', 'economics', 'medical_health', 'literature', 'academic_research'],
      grammar: ['advanced_passive', 'subjunctive', 'cleft_sentences', 'nominalization', 'discourse_markers', 'ellipsis_substitution']
    }
  },
  'C2': {
    name: 'C2 - Uzman',
    color: 'from-gray-700 to-gray-900',
    topics: {
      vocabulary: ['linguistics', 'advanced_philosophy', 'geopolitics', 'specialized_sciences', 'rhetoric_persuasion', 'cultural_analysis'],
      grammar: ['advanced_inversion', 'complex_modality', 'advanced_conditionals', 'academic_structures', 'idiomatic_grammar', 'stylistic_variation']
    }
  }
};

const LearningPath = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('structured'); // 'structured' or 'free'

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const res = await axios.get(`${API}/exercises/progress`, { withCredentials: true });
      setProgress(res.data);
    } catch (error) {
      console.error('Progress yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const isUnitUnlocked = (level, unitIndex) => {
    if (level === 'A1' && unitIndex === 0) return true; // İlk ünite her zaman açık
    
    // Önceki ünite tamamlanmış mı?
    const levelData = LEARNING_PATH[level];
    if (unitIndex > 0) {
      const prevUnit = levelData.units[unitIndex - 1];
      return progress[prevUnit.id]?.completed;
    }
    
    // Önceki seviye tamamlanmış mı?
    if (levelData.requiresLevel) {
      return progress[`${levelData.requiresLevel}_completed`];
    }
    
    return true;
  };

  const getUnitProgress = (unitId) => {
    return progress[unitId] || { completed: false, score: 0, topicsCompleted: 0 };
  };

  const startTopic = (type, id, level) => {
    if (type === 'vocabulary') {
      navigate(`/exercises/vocabulary/${level}/${id}`);
    } else if (type === 'grammar') {
      navigate(`/grammar-lesson/${level}/${id}`);
    } else if (type === 'tense') {
      navigate(`/grammar-lesson/${level}/${id}`);
    }
  };

  const startUnitQuiz = (unitId) => {
    navigate(`/exercises/quiz/${unitId}`);
  };

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              🎯 İngilizce Öğrenme Yolu
            </h1>
            <p className="text-gray-600">A1'den C2'ye adım adım ilerle</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setActiveTab('structured')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'structured' 
                    ? 'bg-white shadow-md text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🔒 A1-A2 Yapılandırılmış
              </button>
              <button
                onClick={() => setActiveTab('free')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'free' 
                    ? 'bg-white shadow-md text-purple-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🔓 B1-C2 Serbest Erişim
              </button>
            </div>
          </div>

          {/* Structured Learning (A1-A2) */}
          {activeTab === 'structured' && (
            <div className="space-y-8">
              {Object.entries(LEARNING_PATH).map(([levelKey, level]) => {
                const isLevelLocked = level.requiresLevel && !progress[`${level.requiresLevel}_completed`];
                
                return (
                  <Card key={levelKey} className={`border-2 ${level.borderColor} ${isLevelLocked ? 'opacity-60' : ''}`}>
                    <CardHeader className={`${level.bgColor}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${level.color} flex items-center justify-center`}>
                            <span className="text-2xl">{levelKey === 'A1' ? '🌱' : '📚'}</span>
                          </div>
                          <div>
                            <CardTitle className="text-xl">{level.name}</CardTitle>
                            <p className="text-sm text-gray-600">{level.description}</p>
                          </div>
                        </div>
                        {isLevelLocked && (
                          <Badge variant="outline" className="bg-white">
                            <Lock className="w-4 h-4 mr-1" />
                            {level.requiresLevel} tamamla
                          </Badge>
                        )}
                        {progress[`${levelKey}_completed`] && (
                          <Badge className="bg-green-500">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Tamamlandı
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      {/* Units */}
                      <div className="space-y-6">
                        {level.units.map((unit, unitIndex) => {
                          const unlocked = !isLevelLocked && isUnitUnlocked(levelKey, unitIndex);
                          const unitProgress = getUnitProgress(unit.id);
                          const topicsCompleted = unitProgress.topicsCompleted || 0;
                          const totalTopics = unit.topics.length;
                          const progressPercent = (topicsCompleted / totalTopics) * 100;

                          return (
                            <div 
                              key={unit.id}
                              className={`p-4 rounded-xl border-2 ${
                                unlocked 
                                  ? 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md transition-all' 
                                  : 'border-gray-100 bg-gray-50'
                              }`}
                            >
                              {/* Unit Header */}
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  {unlocked ? (
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                      <Play className="w-5 h-5 text-blue-600" />
                                    </div>
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                      <Lock className="w-5 h-5 text-gray-400" />
                                    </div>
                                  )}
                                  <div>
                                    <h3 className="font-bold text-lg">{unit.name}</h3>
                                    <div className="flex items-center gap-3 text-sm text-gray-500">
                                      <span className="flex items-center gap-1">
                                        <BookOpen className="w-4 h-4" />
                                        {totalTopics} konu
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Zap className="w-4 h-4 text-yellow-500" />
                                        {unit.milesReward} Mil
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {unitProgress.completed && (
                                  <Badge className="bg-green-500">
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    %{unitProgress.score}
                                  </Badge>
                                )}
                              </div>

                              {/* Progress Bar */}
                              {unlocked && (
                                <div className="mb-4">
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">İlerleme</span>
                                    <span className="font-semibold">{topicsCompleted}/{totalTopics}</span>
                                  </div>
                                  <Progress value={progressPercent} className="h-2" />
                                </div>
                              )}

                              {/* Topics Grid */}
                              {unlocked && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                                  {unit.topics.map((topic, idx) => (
                                    <button
                                      key={idx}
                                      onClick={() => startTopic(topic.type, topic.id, levelKey)}
                                      className="p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 transition-all text-left"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-xl">{topic.icon}</span>
                                        <span className="text-sm font-medium truncate">{topic.name}</span>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}

                              {/* Unit Quiz Button */}
                              {unlocked && progressPercent >= 100 && !unitProgress.completed && (
                                <Button 
                                  onClick={() => startUnitQuiz(unit.id)}
                                  className="w-full bg-gradient-to-r from-green-500 to-green-600"
                                >
                                  <Trophy className="w-4 h-4 mr-2" />
                                  Ünite Sınavına Gir
                                </Button>
                              )}

                              {/* Beceri Testleri - Her ünitede */}
                              {unlocked && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <p className="text-xs text-gray-500 mb-2">Beceri Testleri</p>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => navigate(`/skill-test/${levelKey}/speaking`)}
                                      className="flex-1 py-2 px-3 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-sm font-medium transition-all"
                                    >
                                      🎤 Speaking
                                    </button>
                                    <button
                                      onClick={() => navigate(`/skill-test/${levelKey}/listening`)}
                                      className="flex-1 py-2 px-3 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-sm font-medium transition-all"
                                    >
                                      🎧 Listening
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Level Final Exam */}
                      {!isLevelLocked && level.units.every((u, i) => getUnitProgress(u.id).completed) && (
                        <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Award className="w-10 h-10 text-yellow-600" />
                              <div>
                                <h4 className="font-bold">{levelKey} Seviye Sınavı</h4>
                                <p className="text-sm text-gray-600">{level.finalExam.questions} soru, %{level.finalExam.minScore} geçme notu</p>
                              </div>
                            </div>
                            <Button className="bg-gradient-to-r from-yellow-500 to-orange-500">
                              Sınava Gir
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Free Access (B1-C2) */}
          {activeTab === 'free' && (
            <div className="space-y-6">
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Languages className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-purple-800">Serbest Erişim Modu</h3>
                    <p className="text-sm text-purple-600">B1-C2 seviyelerinde istediğin konuya erişebilirsin</p>
                  </div>
                </div>
              </div>

              {Object.entries(FREE_ACCESS_LEVELS).map(([levelKey, level]) => (
                <Card key={levelKey} className="border-2 hover:shadow-lg transition-all">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${level.color} flex items-center justify-center text-white font-bold`}>
                        {levelKey}
                      </div>
                      <CardTitle>{level.name}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      {/* Vocabulary */}
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          Kelime
                        </h4>
                        <div className="space-y-2">
                          {level.topics.vocabulary?.slice(0, 4).map((topic, idx) => (
                            <button
                              key={idx}
                              onClick={() => navigate(`/exercises/vocabulary/${levelKey}/${topic}`)}
                              className="w-full text-left p-2 bg-white rounded-lg text-sm hover:bg-blue-100 transition-colors"
                            >
                              {topic.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Grammar */}
                      <div className="p-4 bg-green-50 rounded-xl">
                        <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                          <GraduationCap className="w-4 h-4" />
                          Gramer
                        </h4>
                        <div className="space-y-2">
                          {level.topics.grammar?.slice(0, 4).map((topic, idx) => (
                            <button
                              key={idx}
                              onClick={() => navigate(`/exercises/grammar/${levelKey}/${topic}`)}
                              className="w-full text-left p-2 bg-white rounded-lg text-sm hover:bg-green-100 transition-colors"
                            >
                              {topic.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tenses */}
                      {level.topics.tenses && (
                        <div className="p-4 bg-purple-50 rounded-xl">
                          <h4 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                            <Timer className="w-4 h-4" />
                            Zamanlar
                          </h4>
                          <div className="space-y-2">
                            {level.topics.tenses?.slice(0, 4).map((topic, idx) => (
                              <button
                                key={idx}
                                onClick={() => navigate(`/exercises/grammar/${levelKey}/${topic}`)}
                                className="w-full text-left p-2 bg-white rounded-lg text-sm hover:bg-purple-100 transition-colors"
                              >
                                {topic.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => navigate(`/exercises?level=${levelKey}`)}
                    >
                      Tüm {levelKey} Konularını Gör
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </CabinCrewLayout>
  );
};

export default LearningPath;
